import NavLink from "@/navbar/navlink";

export default class Navbar {
	// singleton pattern

	#navDiv;
	#linkObjects;
	#throttle;
	#currentLinkIndex;
	#navLinkSelector;
	#linkListSelector;

	constructor(
		navDiv,
		linkObjects = [],
		throttle = 100,
		currentLinkIndex = 0,
		navLinkSelector = ".js-anchor-link",
		linkListSelector = "nav ul"
	) {
		if (Navbar.singleton) {
			return Navbar.singleton;
		}

		this.#navDiv = navDiv;
		this.#linkObjects = linkObjects;
		this.#throttle = throttle;
		this.#currentLinkIndex = currentLinkIndex;
		this.#navLinkSelector = navLinkSelector;
		this.#linkListSelector = linkListSelector;

		Navbar.singleton = this;
		return Navbar.singleton;
	}

	// getters

	get navDiv() {
		return this.#navDiv;
	}
	get linkObjects() {
		return this.#linkObjects;
	}
	get throttle() {
		return this.#throttle;
	}
	get currentLinkIndex() {
		return this.#currentLinkIndex;
	}

	// private methods

	#getLinks() {
		this.#linkObjects = Array.from(
			this.navDiv.querySelectorAll(this.#navLinkSelector)
		).map((navLink, i) => {
			const current = i === 0;
			const anchorElem = document.querySelector(navLink.hash);
			const navLinkObj = new NavLink(
				navLink,
				anchorElem,
				navLink.hash,
				current
			);
			navLinkObj.init();
			return navLinkObj;
		});
	}

	#horizontalScrollToLink(link) {
		// scrolls horizontally within navbar until link is fully visible
		const scrollableDiv = this.navDiv.querySelector(this.#linkListSelector);
		try {
			scrollableDiv.scroll({
				left: link.offsetLeft - 40,
				top: 100,
				behavior: "smooth",
			}); // -40 corrects for white arrows that were stolen from Youtube
		} catch {
			// for Safari
			scrollableDiv.scroll(link.offsetLeft, 0);
		}
	}

	async #handleLinkClick(clickedLinkObj) {
		// vertical scroll
		const linkObject = this.#linkObjects.find(
			(linkObj) => linkObj.linkElem === clickedLinkObj.linkElem
		);
		await linkObject.scrollToAnchor();

		// set clicked link as current
		this.refreshNavCurrentLink(clickedLinkObj);
	}

	#setScrollListener() {
		let throttleTimer;
		const throttleDelay = 50;

		const scrollFunc = () => {
			this.refreshNavCurrentLink();
		};

		const throttledScrollFunc = () => {
			if (!throttleTimer) {
				scrollFunc.apply(this);
				throttleTimer = setTimeout(() => {
					throttleTimer = null;
				}, throttleDelay);
			}
		};

		window.addEventListener("scroll", throttledScrollFunc, { passive: true });
	}

	#setLinkListeners() {
		Array.from(this.#linkObjects).forEach((linkObj) => {
			linkObj.linkElem.addEventListener("click", async (e) => {
				e.preventDefault();
				await this.#handleLinkClick(linkObj);
			});
		});
	}

	#refreshAnchorBorders() {
		const anchorObjects = this.#linkObjects.map((linkObj) => linkObj.anchor);

		// refresh upper borders
		anchorObjects.forEach((anchorObj) => {
			anchorObj.upperBorder = anchorObj.anchorElem.getBoundingClientRect().top;
		});

		// sort by upper borders
		const sortedAnchorObjects = anchorObjects.sort(
			(a, b) => a["upperBorder"] - b["upperBorder"]
		);

		// refresh lower borders using upper borders
		sortedAnchorObjects.forEach((anchorObj, i) => {
			if (i === sortedAnchorObjects.length - 1) {
				anchorObj.lowerBorder = document.body.offsetHeight - window.scrollY;
			} else {
				anchorObj.lowerBorder = sortedAnchorObjects[i + 1].upperBorder;
			}
		});
	}

	#refreshCurrentIndex() {
		this.#refreshAnchorBorders();

		const anchorObjects = this.#linkObjects.map((linkObj) => linkObj.anchor);

		anchorObjects.forEach((anchorObj, i) => {
			if (anchorObj.upperBorder <= 0 && anchorObj.lowerBorder > 0) {
				if (this.#currentLinkIndex === i) return; // return if index didn't change, else assign and scroll
				this.#currentLinkIndex = i;
				this.#horizontalScrollToLink(this.#linkObjects[i].linkElem);
			}
		});
	}

	#setArrowListeners() {
		const ul = this.#navDiv.querySelector("ul");
		const leftArrowBtn = this.#navDiv.querySelector(
			".navbar-arrow.left .navbar-arrow-btn"
		);
		const rightArrowBtn = this.#navDiv.querySelector(
			".navbar-arrow.right .navbar-arrow-btn"
		);

		const btnHandler = (side) => {
			const diff = side === "left" ? -150 : 150;
			ul.scroll({ left: ul.scrollLeft + diff, top: 0, behavior: "smooth" });
		};

		leftArrowBtn.addEventListener("click", () => {
			btnHandler("left");
		});
		rightArrowBtn.addEventListener("click", () => {
			btnHandler("right");
		});

		let throttleTimer;
		const throttleDelay = 50;

		ul.addEventListener("scroll", () => {
			clearTimeout(throttleTimer);
			throttleTimer = setTimeout(() => {
				this.#refreshNavbarArrows();
			}, throttleDelay);
		});
	}

	#setListeners() {
		this.#setScrollListener();
		this.#setLinkListeners();
		this.#setArrowListeners();
		this.#initializeSizeAdjustmentListener();
	}

	#refreshNavbarArrows() {
		const ul = this.#navDiv.querySelector("ul");
		const leftArrow = this.#navDiv.querySelector(".navbar-arrow.left");
		const rightArrow = this.#navDiv.querySelector(".navbar-arrow.right");
		if (ul.scrollLeft < 5) {
			leftArrow.style.display = "none";
		} else {
			leftArrow.style.display = "flex";
		}
		const scrollRight = ul.scrollWidth - (ul.scrollLeft + ul.clientWidth) + 1;
		if (scrollRight < 5) {
			rightArrow.style.display = "none";
		} else {
			rightArrow.style.display = "flex";
		}
	}

	#initializeSizeAdjustmentListener() {
		// This throttles the event listener. Responding to each resize event is awful for performance. Instead it responds only every fixed period of time (200ms)
		const makeSizeAdjustments = () => {
			window.removeEventListener("resize", makeSizeAdjustments);
			this.#refreshNavbarArrows();
			setTimeout(() => {
				window.addEventListener("resize", makeSizeAdjustments, {
					passive: true,
				});
			}, 100);
		};
		window.addEventListener("resize", makeSizeAdjustments);
	}

	// public methods

	refreshNavCurrentLink(providedLinkObj = null) {
		this.#refreshCurrentIndex();

		const currentLinkObj =
			providedLinkObj || this.#linkObjects[this.#currentLinkIndex];

		this.#linkObjects.forEach((linkObj) => {
			if (currentLinkObj.linkElem === linkObj.linkElem)
				linkObj.setCurrent(true);
			else linkObj.setCurrent(false);
		});
	}

	async setCurrentLinkIndex(newIndex) {
		await this.#handleLinkClick(this.#linkObjects[newIndex]);
	}

	init() {
		this.#getLinks();
		this.refreshNavCurrentLink();
		this.#setListeners();
		this.#refreshNavbarArrows();
	}
}
